import React, { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { MdClose } from 'react-icons/md';
import { CiEdit } from "react-icons/ci";
import { MdOutlineDeleteOutline } from "react-icons/md";
import axiosOrganizationInstance from "./AxiosOrganizationInstance";
import { getStoredUserId } from "./organizationStorage";
import { confirmAction } from "../restaurant/ui/feedback";

function OrganizationImages({ organizationId, status, editMode }) {
    const [images, setImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [isImageFormVisible, setIsImageFormVisible] = useState(false);
    const [editingImage, setEditingImage] = useState(null);
    const [editImageForm, setEditImageForm] = useState({
        imageType: "",
        isPrimary: false
    });

    // Fetch images from API
    useEffect(() => {
        const fetchImages = async () => {
            if (!organizationId || organizationId === "null") {
                return;
            }

            try {
                const token = localStorage.getItem("Token");
                const response = await axiosOrganizationInstance.get(
                    `/organization/${organizationId}/images`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                
                // API returns: { images: [...], organizationId, organizationName, totalImages }
                const imagesData = response.data?.images || [];
                setImages(imagesData);
            } catch (error) {
                console.error("Error fetching images:", error);
                setImages([]);
            }
        };

        if (organizationId) {
            fetchImages();
        }
    }, [organizationId]);

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            newImages.forEach(image => {
                if (image.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        };
    }, [newImages]);

    // Image management handlers
    const handleImageFileChange = (e) => {
        const files = Array.from(e.target.files);
        const imageObjects = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            description: "",
            imageType: "gallery",
            isPrimary: false
        }));
        setNewImages([...newImages, ...imageObjects]);
        e.target.value = ""; // Reset file input
    };

    const handleRemoveNewImage = (index) => {
        const updatedImages = newImages.filter((_, i) => i !== index);
        if (newImages[index].preview) {
            URL.revokeObjectURL(newImages[index].preview);
        }
        setNewImages(updatedImages);
    };

    const handleNewImageFieldChange = (index, field, value) => {
        const updatedImages = [...newImages];
        updatedImages[index][field] = value;
        setNewImages(updatedImages);
    };

    const handleUploadImages = async () => {
        if (newImages.length === 0) {
            alert("Please select at least one image to upload");
            return;
        }

        try {
            const token = localStorage.getItem("Token");
            const userId = getStoredUserId();
            
            if (!token || !organizationId) {
                alert("Error: Missing authentication or organization ID");
                return;
            }

            // Check current image count before uploading
            let currentImageCount = 0;
            try {
                const currentImagesResponse = await axiosOrganizationInstance.get(
                    `/organization/${organizationId}/images`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                currentImageCount = currentImagesResponse.data?.images?.length || 0;
            } catch (err) {
                console.error("Error fetching current images:", err);
            }

            // Check if adding new images would exceed the limit
            if (currentImageCount + newImages.length > 3) {
                alert(`You can only upload 3 images per organization.`);
                return;
            }

            // Upload each image separately
            for (const image of newImages) {
                const formData = new FormData();
                formData.append("image", image.file);
                formData.append("type", "file");
                formData.append("organizationId", organizationId);
                formData.append("imageType", image.imageType);
                formData.append("isPrimary", image.isPrimary ? "True" : "False");
                formData.append("uploadedById", userId || "");

                await axiosOrganizationInstance.post(
                    "/organization/images",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            }

            alert("Images uploaded successfully!");
            // Clear new images and refresh images list
            newImages.forEach(img => {
                if (img.preview) URL.revokeObjectURL(img.preview);
            });
            setNewImages([]);
            setIsImageFormVisible(false);
            
            // Refresh images list
            const response = await axiosOrganizationInstance.get(
                `/organization/${organizationId}/images`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setImages(response.data?.images || []);
        } catch (error) {
            console.error("Error uploading images:", error);
            
            // Check for specific error messages from the API
            const errorMessage = error.response?.data?.error || error.response?.data?.message;
            
            if (errorMessage && errorMessage.includes("Image upload limit reached")) {
                alert(errorMessage);
            } else if (error.response?.status === 400) {
                alert(errorMessage || "Bad request. Please check your input and try again.");
            } else {
                alert("Error uploading images. Please try again.");
            }
        }
    };

    const handleDeleteImage = async (imageId) => {
        const ok = await confirmAction({
            title: "Delete image?",
            message: "Are you sure you want to delete this image?",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;

        try {
            const token = localStorage.getItem("Token");
            
            await axiosOrganizationInstance.delete(
                `/organization/images/${imageId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            alert("Image deleted successfully!");
            // Refresh images list
            try {
                const response = await axiosOrganizationInstance.get(
                    `/organization/${organizationId}/images`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                setImages(response.data?.images || []);
            } catch (fetchError) {
                console.error("Error refreshing images list:", fetchError);
                setImages(images.filter(img => img.imageId !== imageId));
            }
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Error deleting image. Please try again.");
        }
    };

    const handleEditImage = (image) => {
        setEditingImage(image);
        setEditImageForm({
            imageType: image.imageType || "gallery",
            isPrimary: image.isPrimary || false
        });
    };

    const handleCancelEdit = () => {
        setEditingImage(null);
        setEditImageForm({
            imageType: "",
            isPrimary: false
        });
    };

    const handleUpdateImage = async () => {
        if (!editingImage) {
            return;
        }

        try {
            const token = localStorage.getItem("Token");
            
            await axiosOrganizationInstance.put(
                `/organization/images/${editingImage.imageId}`,
                {
                    imageType: editImageForm.imageType,
                    isPrimary: editImageForm.isPrimary
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            alert("Image updated successfully!");
            
            // Refresh images list
            const response = await axiosOrganizationInstance.get(
                `/organization/${organizationId}/images`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setImages(response.data?.images || []);
            
            // Close edit form
            handleCancelEdit();
        } catch (error) {
            console.error("Error updating image:", error);
            const errorMessage = error.response?.data?.error || error.response?.data?.message;
            alert(errorMessage || "Error updating image. Please try again.");
        }
    };

    return (
        <div style={{ marginTop: "30px"}}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h5 style={{ margin: 0 }}>Organization Images</h5>
                {!editMode && status !== "INACTIVE" && (
                    <Button
                        variant="outline-dark"
                        onClick={() => {
                            if (!isImageFormVisible) {
                                // Show alert when opening the form
                                const currentCount = images.length;
                                const remainingSlots = 3 - currentCount;
                                if (remainingSlots <= 0) {
                                    alert("Image upload limit reached. Maximum 3 images allowed per organization.");
                                    return;
                                }
                            }
                            setIsImageFormVisible(!isImageFormVisible);
                        }}
                        className="me-2"
                        disabled={status === "INACTIVE" || !organizationId || organizationId === "null"}
                        style={{
                            width: "90px",
                            backgroundColor: (status === "INACTIVE" || !organizationId || organizationId === "null") ? "#e9ecef" : "#FFFEF0",
                            border: "1px solid #0976A1",
                            color: (status === "INACTIVE" || !organizationId || organizationId === "null") ? "#6c757d" : "#017EF3",
                            cursor: (status === "INACTIVE" || !organizationId || organizationId === "null") ? "not-allowed" : "pointer",
                        }}
                    >
                        {isImageFormVisible ? "Cancel" : "Add"}
                    </Button>
                )}
            </div>

            {/* Image Upload Form */}
            {isImageFormVisible && status !== "INACTIVE" && (
                <div style={{ 
                    border: "1px solid #ddd", 
                    borderRadius: "4px", 
                    padding: "20px", 
                    marginBottom: "20px",
                    backgroundColor: "#f9f9f9"
                }}>
                    <Form.Group className="mb-3">
                        <Form.Label>Select Images</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageFileChange}
                            disabled={status === "INACTIVE"}
                        />
                        <Form.Text className="text-muted">
                            You can select multiple images at once
                        </Form.Text>
                    </Form.Group>

                    {/* Display selected images with options */}
                    {newImages.length > 0 && (
                        <div className="row mt-3">
                            {newImages.map((image, index) => (
                                <div key={index} className="col-md-4 mb-3">
                                    <div className="card" style={{ padding: "10px", position: "relative" }}>
                                        {image.preview && (
                                            <div style={{ position: "relative" }}>
                                                <img
                                                    src={image.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        height: "200px",
                                                        objectFit: "cover",
                                                        marginBottom: "10px",
                                                        borderRadius: "4px",
                                                    }}
                                                />
                                                {status !== "INACTIVE" && (
                                                    <MdClose
                                                        onClick={() => handleRemoveNewImage(index)}
                                                        style={{
                                                            position: "absolute",
                                                            top: "5px",
                                                            right: "5px",
                                                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                                                            color: "white",
                                                            borderRadius: "50%",
                                                            padding: "4px",
                                                            fontSize: "20px",
                                                            cursor: "pointer",
                                                            zIndex: 10
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        
                                        <Form.Group className="mb-2">
                                            <Form.Label style={{ fontSize: "12px" }}>
                                                Image Type
                                            </Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={image.imageType}
                                                onChange={(e) =>
                                                    handleNewImageFieldChange(index, "imageType", e.target.value)
                                                }
                                                disabled={status === "INACTIVE"}
                                            >
                                                <option value="gallery">Gallery</option>
                                                <option value="logo">Logo</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <div style={{ fontSize: "11px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Set as Primary"
                                                    checked={image.isPrimary}
                                                    onChange={(e) =>
                                                        handleNewImageFieldChange(index, "isPrimary", e.target.checked)
                                                    }
                                                    disabled={status === "INACTIVE"}
                                                    style={{ fontSize: "11px", marginBottom: "0" }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {newImages.length > 0 && (
                        <Button
                            variant="primary"
                            onClick={handleUploadImages}
                            disabled={status === "INACTIVE"}
                            style={{
                                backgroundColor: "#017EF3",
                                border: "none",
                                marginTop: "10px"
                            }}
                        >
                            Upload Images
                        </Button>
                    )}
                </div>
            )}

            {/* Display existing images */}
            {images.length > 0 ? (
                <div className="row">
                    {images.map((image, index) => (
                        <div key={image.imageId || index} className="col-md-4 mb-3">
                            <div className="card" style={{ padding: "10px", position: "relative" }}>
                                {(image.imageUrl || image.imagePath) && (
                                    <div style={{ position: "relative" }}>
                                        <img
                                            src={image.imageUrl || image.imagePath}
                                            alt={image.imageName || `Image ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: "200px",
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                                marginBottom: "10px",
                                            }}
                                        />
                                        {image.isPrimary && (
                                            <span style={{
                                                position: "absolute",
                                                top: "5px",
                                                left: "5px",
                                                backgroundColor: "#28a745",
                                                color: "white",
                                                padding: "2px 8px",
                                                borderRadius: "3px",
                                                fontSize: "11px",
                                                fontWeight: "bold"
                                            }}>
                                                Primary
                                            </span>
                                        )}
                                    </div>
                                )}
                                {editingImage?.imageId === image.imageId ? (
                                    <div style={{ 
                                        border: "1px solid #ddd", 
                                        borderRadius: "4px", 
                                        padding: "15px", 
                                        marginTop: "10px",
                                        backgroundColor: "#f9f9f9"
                                    }}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ fontSize: "12px" }}>
                                                Image Type
                                            </Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={editImageForm.imageType}
                                                onChange={(e) =>
                                                    setEditImageForm({ ...editImageForm, imageType: e.target.value })
                                                }
                                                disabled={status === "INACTIVE"}
                                            >
                                                <option value="gallery">Gallery</option>
                                                <option value="logo">Logo</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <div style={{ fontSize: "11px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Set as Primary"
                                                    checked={editImageForm.isPrimary}
                                                    onChange={(e) =>
                                                        setEditImageForm({ ...editImageForm, isPrimary: e.target.checked })
                                                    }
                                                    disabled={status === "INACTIVE"}
                                                    style={{ fontSize: "11px", marginBottom: "0" }}
                                                />
                                            </div>
                                        </Form.Group>
                                        <div style={{ display: "flex", gap: "10px" }}>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleUpdateImage}
                                                disabled={status === "INACTIVE"}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: "#017EF3",
                                                    border: "none"
                                                }}
                                            >
                                                Update
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleCancelEdit}
                                                style={{ flex: 1 }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ 
                                            fontSize: "12px", 
                                            marginBottom: "5px",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}>
                                            <span>
                                                <strong>Type:</strong> {image.imageType || "N/A"}
                                            </span>
                                            {status !== "INACTIVE" && (
                                                <div style={{ 
                                                    display: "flex", 
                                                    alignItems: "center", 
                                                    gap: "15px"
                                                }}>
                                                    <CiEdit
                                                        onClick={() => handleEditImage(image)}
                                                        style={{
                                                            fontSize: "24px",
                                                            color: "#017EF3",
                                                            cursor: "pointer",
                                                            opacity: 1,
                                                        }}
                                                    />
                                                    <MdOutlineDeleteOutline
                                                        onClick={() => handleDeleteImage(image.imageId)}
                                                        style={{
                                                            fontSize: "24px",
                                                            color: "#FF3B30",
                                                            cursor: "pointer",
                                                            opacity: 1,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ 
                    padding: "20px", 
                    textAlign: "center", 
                    color: "#6c757d",
                    border: "1px dashed #ddd",
                    borderRadius: "4px"
                }}>
                    No images uploaded yet. Click "Add" to upload.
                </div>
            )}
        </div>
    );
}

export default OrganizationImages;

